import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy } from 'lucide-react';
import { Botao } from '../../../web/src/componentes/base';
import { AssinaturaMalgo } from '../../../web/src/componentes/assinatura';
import { numero } from '../../../web/src/nucleo/formato';

/**
 * Faixa vermelha do Sandbox (mesmo token da faixa de ambiente do MalgoOS) e a da visualização da equipe. Spec r23 (O4): a
 * mesma faixa nas telas públicas (entrar, convite, ver como) do endereço do Sandbox, antes do login.
 */
export function FaixaAmbiente({ avisos }: { avisos: string[] }) {
  const ativa = avisos.length > 0;
  useEffect(() => {
    const raiz = document.documentElement;
    if (ativa) raiz.dataset.faixaAmbiente = '';
    else delete raiz.dataset.faixaAmbiente;
    return () => {
      delete raiz.dataset.faixaAmbiente;
    };
  }, [ativa]);
  if (!ativa) return null;
  return (
    <div role="status" data-faixa-ambiente className="faixa-ambiente fixed inset-x-0 top-0 z-40 flex items-center justify-center gap-2 overflow-hidden bg-ambiente px-3 text-xs font-semibold text-ambiente-tinta">
      {avisos.map((a, k) => (
        <span key={a} className="truncate" title={a}>
          {k > 0 && <span aria-hidden className="mr-2">·</span>}
          {a}
        </span>
      ))}
    </div>
  );
}

/** A marca do app do cliente: a assinatura oficial da Malgo, sem o "OS". */
export function Marca({ grande }: { grande?: boolean }) {
  const { t } = useTranslation('portal');
  return (
    <div className="flex items-center gap-2 text-tinta-1">
      <AssinaturaMalgo comOS={false} className={grande ? 'h-9' : 'h-8'} />
      <span className="sr-only">{t('app.nome')}</span>
    </div>
  );
}

/** Copiar para a área de transferência (Pix copia e cola, linha digitável), com o retorno "Copiado" anunciado. */
export function BotaoCopiar({ texto, rotulo, copiado }: { texto: string; rotulo: string; copiado: string }) {
  const [feito, setFeito] = useState(false);
  return (
    <Botao
      icone={feito ? <Check className="size-4" aria-hidden /> : <Copy className="size-4" aria-hidden />}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(texto);
        } catch {
          // Sem a API (webview antiga): seleciona o texto para copiar à mão.
          const area = document.createElement('textarea');
          area.value = texto;
          document.body.appendChild(area);
          area.select();
          document.execCommand('copy');
          area.remove();
        }
        setFeito(true);
        setTimeout(() => setFeito(false), 2500);
      }}
    >
      <span aria-live="polite">{feito ? copiado : rotulo}</span>
    </Botao>
  );
}

/** Fuso das datas do portal: o do escritório (São Paulo), como a equipe vê no MalgoOS. */
export const FUSO_PORTAL = 'America/Sao_Paulo';

const localeDe = (idioma: string) => (idioma === 'pt' ? 'pt-BR' : idioma);

/**
 * Data no formato do idioma. AAAA-MM-DD sem hora (vencimento, emissão) sai como está, sem fuso. Instante com hora
 * (`publicado_em`, `created_at`, passkey, termo) sai no dia de São Paulo: review r23 visual (M5), antes o instante era
 * cortado em UTC e o que acontecia depois das 21 h aparecia no dia seguinte.
 */
export function dataCurta(iso: string | null | undefined, idioma: string) {
  if (!iso) return '';
  const opcoes = { day: '2-digit', month: '2-digit', year: 'numeric' } as const;
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    const [a, m, d] = iso.split('-').map(Number);
    if (!a || !m || !d) return '';
    return new Intl.DateTimeFormat(localeDe(idioma), { ...opcoes, timeZone: 'UTC' }).format(Date.UTC(a, m - 1, d));
  }
  const instante = new Date(iso);
  if (Number.isNaN(instante.getTime())) return '';
  return new Intl.DateTimeFormat(localeDe(idioma), { ...opcoes, timeZone: FUSO_PORTAL }).format(instante);
}

export function competenciaCurta(iso: string | null | undefined, idioma: string) {
  if (!iso) return '';
  const [a, m] = iso.slice(0, 7).split('-').map(Number);
  if (!a || !m) return '';
  return new Intl.DateTimeFormat(idioma === 'pt' ? 'pt-BR' : idioma, { month: '2-digit', year: 'numeric', timeZone: 'UTC' }).format(Date.UTC(a, m - 1, 1));
}

/** Tamanho do arquivo com o separador decimal do idioma (review r23 visual, B2: "1,5 MB" em pt e es, "1.5 MB" em en). */
export function tamanhoArquivo(bytes: number) {
  if (bytes < 1024) return `${numero(bytes)} B`;
  if (bytes < 1024 * 1024) return `${numero(Math.round(bytes / 1024))} KB`;
  return `${numero(bytes / 1024 / 1024, 1)} MB`;
}
